// PWA — Smart name fallbacks for unknown contacts, per R11 ChatGPT consult
// 2026-05-06. Replaces "Unknown" / raw phone with a labeled human-readable
// proxy so the conversations list never looks broken.
//
// Hierarchy (first non-empty wins):
//   1. Real name from contact (contact.firstName + lastName), if not
//      "Unknown" / empty / placeholder
//   2. Service-type label ("Ceramic Coating Lead") when we know what they
//      asked about
//   3. State label by area code ("New CT Lead" / "New NY Lead")
//   4. Generic ("New Lead")
//
// Phone number stays as the secondary line in the UI — caller doesn't pass
// it through here, it's rendered separately.

const PLACEHOLDER_NAMES = new Set([
  '', 'unknown', 'unknown unknown', 'no name', 'noname', 'guest',
  'guest visitor', 'guest visitor 001', 'caller',
]);

function looksLikePlaceholderName(raw: string): boolean {
  const lower = raw.trim().toLowerCase();
  if (!lower) return true;
  if (PLACEHOLDER_NAMES.has(lower)) return true;
  // "Unknown 1234" / "Caller xxx"
  if (/^(unknown|caller|guest)\b/i.test(raw)) return true;
  return false;
}

export interface NameFallbackInput {
  contactName?: string | null;
  contactPhone?: string | null;
  serviceType?: string | null;
}

/**
 * Resolve a display name for a contact. Returns an object with the friendly
 * label plus a flag indicating whether this was a real name (so callers can
 * style "real name" vs "fallback label" differently if desired).
 */
export function resolveDisplayName(input: NameFallbackInput): {
  displayName: string;
  isFallback: boolean;
} {
  const raw = (input.contactName ?? '').trim();

  if (raw && !looksLikePlaceholderName(raw)) {
    return { displayName: raw, isFallback: false };
  }

  // Service-type label (when we know what they asked about)
  const svc = (input.serviceType ?? '').trim().toLowerCase();
  if (svc) {
    const titled = svc
      .split(/\s+/)
      .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(' ');
    return { displayName: `${titled} Lead`, isFallback: true };
  }

  // Generic. We deliberately DON'T do area-code → state lookups
  // ("New CT Lead" looks like a system label, not a person — Ernie
  // 2026-05-06). Plain "New Lead" reads cleaner.
  return { displayName: 'New Lead', isFallback: true };
}

/** Format a phone number to "(203) 806-0286" style for display. */
export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}
